/**
 * AdminConsole — single tabbed admin shell at /admin.
 *
 * Consolidates every admin feature into one navigable surface so admins
 * don't have to hunt around the app for settings, bookings, marketing, etc.
 *
 * Tabs (in workflow order):
 *   1. Overview   — at-a-glance status (assessments, bookings, AI health)
 *   2. Settings   — consultant/booking config + pricing (operations admin)
 *   3. Bookings   — upcoming confirmed bookings
 *   4. Testimonials — published testimonials
 *   5. Marketing  — SEO/AEO/Meta-Ads (embeds MarketingPanel)
 *   6. AI Status  — Universal-Key health (embeds AiStatusPanel)
 *   7. Efforts    — link out to /admin/efforts (heavy GDPR-tracking page)
 *
 * Tab is URL-controlled via ?tab=marketing so admins can deep-link or
 * bookmark a specific tab.
 *
 * Designed for portability — the only app-specific imports are the existing
 * Account admin cards. Drop this file into a sibling Emergent app, wire the
 * /admin route, and it just works.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { safeTz } from "@/lib/timezone";
import {
  LayoutDashboard,
  CreditCard,
  Settings as SettingsIcon,
  CalendarDays,
  MessagesSquare,
  UsersRound,
  Megaphone,
  Activity,
  ShieldAlert,
  ExternalLink,
  Sparkles,
  BrainCircuit,
  ListChecks,
  ScrollText,
} from "lucide-react";
import AdminSettingsCard from "@/components/account/AdminSettingsCard";
import AdminBookingsCard from "@/components/account/AdminBookingsCard";
import AdminTestimonialsCard from "@/components/account/AdminTestimonialsCard";
import AiStatusPanel from "@/components/AiStatusPanel";
import AiPromptsPanel from "@/components/admin/AiPromptsPanel";
import QuestionsPanel from "@/components/admin/QuestionsPanel";
import PricingPaymentsPanel from "@/components/admin/PricingPaymentsPanel";
import UsersPanel from "@/components/admin/UsersPanel";
import GovernancePanel from "@/components/admin/GovernancePanel";
import { MarketingPanel } from "@/pages/MarketingConsole";

const TABS = [
  { key: "overview",     label: "Overview",     icon: LayoutDashboard },
  { key: "settings",     label: "Settings",     icon: SettingsIcon },
  { key: "pricing",      label: "Pricing & Payments", icon: CreditCard },
  { key: "bookings",     label: "Bookings",     icon: CalendarDays },
  { key: "testimonials", label: "Testimonials", icon: MessagesSquare },
  { key: "users",        label: "Users",        icon: UsersRound },
  { key: "marketing",    label: "Marketing",    icon: Megaphone },
  { key: "prompts",      label: "AI Prompts",   icon: BrainCircuit },
  { key: "questions",    label: "Questions",    icon: ListChecks },
  { key: "governance",   label: "Governance",   icon: ScrollText },
  { key: "ai_status",    label: "AI Status",    icon: Activity },
  { key: "efforts",      label: "Efforts",      icon: ShieldAlert, external: "/admin/efforts" },
];

// ---------------------------------------------------------------------------
const settingsFromApi = (data) => ({
  consultant_email: data?.consultant_email || "",
  booking_email: data?.booking_email || "",
  booking_timezone: data?.booking_timezone || "Europe/London",
  booking_lead_hours: data?.booking_lead_hours ?? 24,
  booking_horizon_days: data?.booking_horizon_days ?? 21,
  booking_availability: ensureUids(data?.booking_availability),
  paywall_effective_from: data?.paywall_effective_from || "",
  advisor_toolkit_url: data?.advisor_toolkit_url || "",
});

function ensureUids(rows) {
  return (Array.isArray(rows) ? rows : []).map((r) =>
    r && r._uid ? r : { ...r, _uid: `av_${Math.random().toString(36).slice(2, 10)}` }
  );
}

function stripUids(rows) {
  return (rows || []).map(({ _uid, ...rest }) => rest); // eslint-disable-line no-unused-vars
}

// ---------------------------------------------------------------------------
export default function AdminConsole() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo(() => {
    const t = searchParams.get("tab") || "overview";
    return TABS.find((x) => x.key === t)?.key || "overview";
  }, [searchParams]);

  // Shared admin data — only loaded once when the user lands on /admin
  const [settingsForm, setSettingsForm] = useState(settingsFromApi({}));
  const [settingsRaw, setSettingsRaw] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [hostTz, setHostTz] = useState("Europe/London");
  const [assessmentCount, setAssessmentCount] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/sign-in?redirect=/admin"); return; }
    if (!user.is_admin) {
      toast.error("Admin access required.");
      navigate("/account");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettingsForm(settingsFromApi(data));
        setSettingsRaw(data);
      } catch (e) { console.warn("settings load failed", e); }
      try {
        const { data } = await api.get("/admin/testimonials");
        setTestimonials(data?.testimonials || []);
      } catch (e) { console.warn("testimonials load failed", e); }
      try {
        const { data } = await api.get("/admin/bookings/upcoming");
        setUpcomingBookings(data?.bookings || []);
        setHostTz(safeTz(data?.host_timezone, "Europe/London"));
      } catch (e) { console.warn("bookings load failed", e); }
      try {
        const { data } = await api.get("/account/assessments");
        setAssessmentCount((data.assessments || []).length);
      } catch (e) { console.warn("assessments count failed", e); }
    })();
  }, [user, authLoading, navigate]);

  const reloadTestimonials = async () => {
    try {
      const { data } = await api.get("/admin/testimonials");
      setTestimonials(data?.testimonials || []);
    } catch (e) { console.warn("testimonials reload failed", e); }
  };

  const saveSettings = async () => {    try {
      setSettingsSaving(true);
      const payload = { ...settingsForm, booking_availability: stripUids(settingsForm.booking_availability) };
      const { data } = await api.post("/settings", payload);
      setSettingsForm(settingsFromApi(data));
      setSettingsRaw(data);
      toast.success("Settings updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings.");
    } finally {
      setSettingsSaving(false);
    }
  };

  if (authLoading || !user?.is_admin) return null;

  const setTab = (key) => setSearchParams({ tab: key }, { replace: true });

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-10 py-10" data-testid="admin-console">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">
              Admin console
            </p>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
              Operations &amp; growth
            </h1>
            <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300">
              Signed in as <span className="text-slate-900 dark:text-slate-50 font-medium">{user?.email}</span>
            </p>
          </div>
          <Link
            to="/account"
            data-testid="admin-back-to-account"
            className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
          >
            ← Back to your Account
          </Link>
        </div>

        {/* Tab bar */}
        <div className="border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
          <nav className="flex items-center gap-1 min-w-max">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.key;
              if (t.external) {
                return (
                  <Link
                    key={t.key}
                    to={t.external}
                    data-testid={`admin-tab-${t.key}`}
                    className="inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 border-transparent -mb-px transition-colors whitespace-nowrap text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                  >
                    <Icon className="h-4 w-4" /> {t.label} <ExternalLink className="h-3 w-3 opacity-50" />
                  </Link>
                );
              }
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                    active
                      ? "border-teal-600 dark:border-teal-500 text-slate-900 dark:text-slate-50"
                      : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                  }`}
                >
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab content */}
        <div className="mt-8" data-testid={`admin-content-${activeTab}`}>
          {activeTab === "overview" && (
            <Overview
              assessmentCount={assessmentCount}
              bookingsCount={upcomingBookings.length}
              testimonialsCount={testimonials.length}
              consultantEmail={settingsForm.consultant_email}
              hostTz={hostTz}
              onJump={setTab}
            />
          )}

          {activeTab === "settings" && (
            <div className="space-y-6" data-testid="admin-settings-tab">
              <AdminSettingsCard
                form={settingsForm}
                setForm={setSettingsForm}
                saving={settingsSaving}
                onSave={saveSettings}
              />
            </div>
          )}

          {activeTab === "bookings" && (
            <AdminBookingsCard bookings={upcomingBookings} hostTz={hostTz} />
          )}

          {activeTab === "testimonials" && (
            <AdminTestimonialsCard testimonials={testimonials} onChanged={reloadTestimonials} />
          )}

          {activeTab === "users" && <UsersPanel />}

          {activeTab === "marketing" && <MarketingPanel />}

          {activeTab === "pricing" && <PricingPaymentsPanel />}

          {activeTab === "prompts" && <AiPromptsPanel />}
          {activeTab === "questions" && <QuestionsPanel />}
          {activeTab === "governance" && <GovernancePanel />}

          {activeTab === "ai_status" && <AiStatusPanel />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
function Overview({
  assessmentCount,
  bookingsCount,
  testimonialsCount,
  consultantEmail,
  hostTz,
  onJump,
}) {
  const tiles = [
    { key: "assessments", label: "Assessments (yours)", value: assessmentCount ?? "…", hint: "Your own dashboard", to: "/account" },
    { key: "bookings", label: "Upcoming bookings", value: bookingsCount, hint: hostTz, onClick: () => onJump("bookings") },
    { key: "testimonials", label: "Testimonials", value: testimonialsCount, hint: "Published", onClick: () => onJump("testimonials") },
    { key: "consultant", label: "Consultant", value: consultantEmail ? "✓" : "—", hint: consultantEmail || "Not set", onClick: () => onJump("settings") },
  ];

  return (
    <div className="space-y-6" data-testid="admin-overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tiles.map((t) => {
          const Wrap = t.to ? Link : "button";
          const wrapProps = t.to ? { to: t.to } : { onClick: t.onClick, type: "button" };
          return (
            <Wrap
              key={t.key}
              {...wrapProps}
              data-testid={`overview-tile-${t.key}`}
              className="text-left rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:border-teal-300 dark:hover:border-teal-700 hover:-translate-y-0.5 transition-all"
            >
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                {t.label}
              </p>
              <p className="mt-2 font-display text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
                {t.value}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 truncate">{t.hint}</p>
            </Wrap>
          );
        })}
      </div>

      <div className="rounded-lg border border-teal-200 dark:border-teal-500/30 bg-teal-50 dark:bg-teal-500/10 p-5 flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-teal-700 dark:text-teal-400 mt-0.5 shrink-0" />
        <div className="text-sm text-teal-900 dark:text-teal-100 leading-relaxed">
          <p className="font-display font-semibold text-slate-900 dark:text-slate-50">
            Quick admin pointers
          </p>
          <ul className="mt-2 space-y-1.5 list-disc list-inside">
            <li><strong>Settings</strong> — change the consultant email, booking window, and pricing in one place.</li>
            <li><strong>Marketing</strong> — edit <code className="font-mono text-xs">llms.txt</code>, meta tags, JSON-LD structured data.</li>
            <li><strong>AI Status</strong> — live Universal-Key health for every Claude call (8s refresh).</li>
            <li><strong>Efforts</strong> — GDPR funnel of abandoned vs completed scans (audit-logged PII reveals).</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
